// src/App.jsx
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { ensureJourney, step as telemetryStep, track, endJourney, STEP_NAMES } from './telemetry/journey.js';
import { simulateWait } from './utils/simulateWait';
import { cleanupPendingCancelSessions, cancelIfStillPreCharge } from './utils/cancelSession';
import {
  Preloader,
  EvProfile,
  AccountChoice,
  ConnectorSelection,
  ChargeSetup,
  VehicleVerification,
  FleetAccessResult,
  AmountPicker,
  MakePayment,
  ConnectCharger,
  ChargeMonitoring,
  DisconnectCharger,
  SessionSummary,
  DebugStepControl,
} from './components';
import { useSession } from './context/SessionContext.jsx';
import { usePreparingNotification } from './hooks/usePreparingNotification';
import { STEPS, STEP_FORWARD, STEP_BACK } from './constants/steps';
import { PAYMENTS_ENABLED, ACCOUNT_CHOICE_ENABLED } from './config/features';
import { shouldShowPaymentStep } from './utils/billingRules';
import { isGuestChoice } from './constants/accountChoice.js';
import { getActiveSession, clearActiveSession } from './utils/sessionPersistence';
import { getCustomerSession } from './api/sessionsApi';
import ActiveSessionBanner from './components/ActiveSessionBanner/ActiveSessionBanner';
import AuthRequiredView from './components/AuthRequired/AuthRequiredView.jsx';
import SessionValidationGate from './components/SessionValidationGate/SessionValidationGate.jsx';
import TabOwnershipBanner from './components/TabOwnershipBanner/TabOwnershipBanner.jsx';
import { useSessionTabOwnership } from './hooks/useSessionTabOwnership.js';
import {
  VALIDATION_PHASES,
  resolveValidationPhase,
  isTerminalRehydrateReason,
  reconnectDelayFor,
} from './utils/sessionValidationPhase.js';

import 'bootstrap/dist/css/bootstrap.min.css';
import './App.less';

// STEP CONFIG
const STEP_MAP = {
  [STEPS.EV_PROFILE]: {
    component: EvProfile,
    navigation: true,
  },

  // Asked before anything is created, because a guest session is created
  // with user_id = NULL and nothing can bind a user to it afterwards.
  [STEPS.ACCOUNT_CHOICE]: {
    component: AccountChoice,
    navigation: true,
  },

  // Conditional: only reached when the charger has >1 USABLE connector
  // at the moment Get Started is tapped. EvProfile owns that routing.
  [STEPS.CONNECTOR_SELECTION]: {
    component: ConnectorSelection,
    navigation: true,
  },

  [STEPS.CHARGE_SETUP]: {
    component: ChargeSetup,
    navigation: true,
  },

  [STEPS.VEHICLE_VERIFICATION]: {
    component: VehicleVerification,
    navigation: true,
  },

  // Conditional: only reached when the backend's access outcome for the
  // verified vehicle is a card fallback, a denial or "no charging flow".
  [STEPS.FLEET_ACCESS_RESULT]: {
    component: FleetAccessResult,
    navigation: true,
  },

  [STEPS.AMOUNT_PICKER]: {
    component: AmountPicker,
    navigation: true,
  },

  [STEPS.MAKE_PAYMENT]: {
    component: MakePayment,
    navigation: true,
  },

  [STEPS.CONNECT_CHARGER]: {
    component: ConnectCharger,
    navigation: false,
  },

  [STEPS.MONITOR_CHARGING]: {
    component: ChargeMonitoring,
    navigation: true,
  },

  [STEPS.DISCONNECT_CHARGER]: {
    component: DisconnectCharger,
    navigation: true,
  },

  [STEPS.SUMMARY]: {
    component: SessionSummary,
    navigation: false,
  },
};

const MIN_STEP = STEPS.EV_PROFILE;
const MAX_STEP = STEPS.SUMMARY;

export default function App() {
  const [loading, setLoading] = useState(true);
  const [activeSessionConflict, setActiveSessionConflict] = useState(null); // { sessionUid, stationId, status }
  // Bootstrap validation phase. Gates whether the charging Screen is
  // safe to render. VALIDATING = backend request in flight; RECONNECTING
  // = transient failure with retry loop running; READY = validation
  // succeeded OR determined we have nothing to validate against.
  //
  // The initial value is READY so an app with NO recovery hint at all
  // renders the start screen straight through the Preloader without
  // pausing on the validating gate. The bootstrap effect below flips
  // this to VALIDATING as soon as it decides there's a hint worth
  // checking against the backend.
  const [validationPhase, setValidationPhase] = useState(VALIDATION_PHASES.READY);
  // Attempt counter for the reconnect backoff schedule; reset every
  // time we successfully reach the backend so a later outage restarts
  // the fast-then-slow pattern from the top.
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const session = useSession();
  const { step, paymentStep, sessionUid, debugMode } = session.state;
  const { setDebugMode, setPaymentStep, rehydrateSession, reset } = session;

  // M4 — Cross-tab ownership. Only one tab controls a given
  // sessionUid; other tabs render read-only until the owner closes
  // or the user explicitly takes over.
  const { role: tabRole, requestOwnership } = useSessionTabOwnership(sessionUid);

  // Journey telemetry: every wizard step, and the receipt as the success end.
  // A direct (non-QR) visit starts a journey on first render of the wizard.
  const stationIdForJourney = session.state.stationId;
  useEffect(() => {
    const name = STEP_NAMES[step];
    if (!name) return;
    ensureJourney(stationIdForJourney || null);
    telemetryStep(name);
    if (name === 'charging') track('charging.monitor.viewed');
    if (name === 'receipt') {
      track('receipt.viewed');
      endJourney('success');
    }
  }, [step, stationIdForJourney]);

  // Observed from session state, so the payment and account code stay untouched.
  const accountChoiceForJourney = session.state.accountChoice;
  useEffect(() => {
    if (accountChoiceForJourney === 'guest' || accountChoiceForJourney === 'account') {
      track('charging.account.selected', { metadata: { choice: accountChoiceForJourney === 'guest' ? 'guest' : 'registered' } });
    }
  }, [accountChoiceForJourney]);
  const paymentStatusForJourney = session.state.payment?.status;
  const paymentErrorCode = session.state.payment?.error?.code;
  useEffect(() => {
    if (paymentStatusForJourney === 'authorized') track('payment.authorization.completed', { result: 'authorized' });
    else if (paymentStatusForJourney === 'error') {
      track('payment.authorization.completed', { result: 'declined', reason: typeof paymentErrorCode === 'string' ? paymentErrorCode : undefined });
    }
  }, [paymentStatusForJourney, paymentErrorCode]);

  // Check if debug panel should be shown (based on URL parameter)
  const showDebugPanel = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === 'true' || params.has('debug');
  }, []);

  // Use refs to avoid recreating callbacks when context value changes
  const setStepRef = useRef(session.setStep);
  const rehydrateSessionRef = useRef(rehydrateSession);

  // Update refs on every render - just ref assignment, no side effects
  setStepRef.current = session.setStep;
  rehydrateSessionRef.current = rehydrateSession;

  // Handle "Resume Session" from active session banner
  const handleResumeActiveSession = useCallback(async () => {
    if (!activeSessionConflict) return;

    const { sessionUid: activeUid, stationId: activeStationId } = activeSessionConflict;
    console.log('[App] User chose to RESUME active session:', activeUid);

    // Set the active session UID into state and trigger rehydration
    session.update({ sessionUid: activeUid, stationId: activeStationId });

    // Update URL to reflect the active session
    const url = new URL(window.location);
    url.searchParams.set('sessionUid', activeUid);
    url.searchParams.set('stationId', activeStationId);
    window.history.replaceState({}, '', url);

    setActiveSessionConflict(null);
    setLoading(true);

    // Wait a tick for state to propagate, then rehydrate
    await new Promise(r => setTimeout(r, 50));
    const result = await rehydrateSessionRef.current();

    if (result.success) {
      console.log('[App] ✅ Resumed active session, step:', result.targetStep);
    } else {
      console.warn('[App] ⚠️ Resume failed:', result.reason, '— allowing fresh start');
      clearActiveSession();
    }

    setLoading(false);
  }, [activeSessionConflict, session]);

  // Handle "Start New Session" — dismiss banner and continue
  const handleDismissActiveSession = useCallback(() => {
    console.log('[App] User chose to START NEW session, dismissing active session banner');
    setActiveSessionConflict(null);
    // Don't clear localStorage — the active session is still running on the charger
    // It will be cleared when that session actually ends
  }, []);

  // ================================================================
  // BOOTSTRAP RECOVERY — The core session restoration logic
  // ================================================================
  // Recovery priority order:
  //   1. URL ?sessionUid param (deep link / QR code / shared link)
  //   2. ev.session.v1 localStorage (main session state, survives refresh)
  //   3. ev_active_session_uid localStorage (lightweight recovery beacon)
  //   4. No session → idle start screen
  //
  // CRITICAL: All recovery paths call markBootstrapComplete() when done.
  // This prevents the URL sync effect from prematurely clearing URL params.
  // ================================================================
  useEffect(() => {
    (async () => {
      // Clean up any pending cancel sessions from previous page session
      await cleanupPendingCancelSessions();

      // Show splash for minimum time
      await simulateWait(600);

      // ── RECOVERY SOURCE 1: URL sessionUid param ──
      const urlSessionUid = new URLSearchParams(window.location.search).get('sessionUid');

      // ── RECOVERY SOURCE 2: Main session state (from load() → localStorage) ──
      const stateSessionUid = sessionUid; // from session.state destructure

      // ── RECOVERY SOURCE 3: Active session beacon (lightweight fallback) ──
      const savedSession = getActiveSession();
      const beaconSessionUid = savedSession?.sessionUid || null;

      // Resolve the best available sessionUid for recovery
      // URL takes priority, then main state, then beacon
      const effectiveSessionUid = urlSessionUid || stateSessionUid || beaconSessionUid;

      console.log('[App] 🔍 Bootstrap recovery sources:', {
        url: urlSessionUid || '(none)',
        state: stateSessionUid || '(none)',
        beacon: beaconSessionUid || '(none)',
        effective: effectiveSessionUid || '(none)'
      });

      // ── NO SESSION ANYWHERE → idle start screen ──
      if (!effectiveSessionUid) {
        console.log('[App] No session found from any source — showing start screen');
        session.markBootstrapComplete();
        // Nothing to validate; leave phase as READY (its initial value)
        // so the Preloader unmounts and the start screen renders.
        setLoading(false);
        return;
      }

      // ── RECOVERY PATH: We have a sessionUid from somewhere ──
      // If the URL had a different sessionUid than state, set it into state
      if (urlSessionUid && urlSessionUid !== stateSessionUid) {
        console.log('[App] URL sessionUid differs from state, updating:', urlSessionUid);
        session.update({ sessionUid: urlSessionUid });
      }

      // If recovering from beacon only (state was wiped by TTL), seed stationId too
      if (!stateSessionUid && beaconSessionUid && savedSession?.stationId) {
        console.log('[App] Recovering from beacon — seeding stationId:', savedSession.stationId);
        // Seed connectorId only when the saved beacon actually
        // carries a valid physical connector id. Legacy beacons
        // that predate the multi-connector picker may have null
        // here — do NOT fabricate "connector 1" for them; the
        // backend rehydrateSession call below overwrites the
        // seeded state with the DB row's real connector_id, so
        // leaving connectorId null here is the correct behaviour.
        const savedConnectorId = Number(savedSession?.connectorId);
        session.update({
          sessionUid: beaconSessionUid,
          stationId: savedSession.stationId,
          connectorId: Number.isInteger(savedConnectorId) && savedConnectorId >= 1
            ? savedConnectorId
            : null,
        });
      }

      // A recovery hint exists — treat the local snapshot as HINT-ONLY
      // and put the UI into the validating state until the backend
      // confirms whether the hinted session is still valid. The
      // charging Screen must not render during this window: stale
      // localStorage cannot be allowed to claim "charging" as fact.
      setValidationPhase(VALIDATION_PHASES.VALIDATING);
      setLoading(false); // Preloader off; SessionValidationGate takes over

      // CRITICAL: Use the override parameter to avoid stateRef race condition.
      // session.update() is async (React batched), so stateRef may not reflect
      // the new sessionUid yet when rehydrateSession reads it.
      console.log('[App] Attempting rehydration with sessionUid:', effectiveSessionUid);
      const result = await rehydrateSessionRef.current(effectiveSessionUid);

      if (result.success) {
        console.log('[App] ✅ Session rehydrated successfully, step:', result.targetStep);
      } else if (result.transient) {
        // Transient failure — DO NOT render the charging Screen as if
        // local state were authoritative. The validation gate stays
        // up in RECONNECTING mode; a background retry effect (below)
        // will flip us back to READY once the backend answers.
        console.warn('[App] ⚠️ Transient error during rehydration — keeping local snapshot as HINT, blocking charging UI');
      } else {
        console.log('[App] Session rehydration failed:', result.reason);
        // Every non-transient failure means the recovered snapshot is
        // no longer valid. Clean up the URL sessionUid param and the
        // recovery beacon for the WHOLE terminal-reason set so we don't
        // leave breadcrumbs that would re-trigger the same failed
        // recovery on the next reload. `rehydrateSession` has already
        // cleared local state via reset() for these reasons.
        if (isTerminalRehydrateReason(result.reason)) {
          if (urlSessionUid) {
            const url = new URL(window.location);
            url.searchParams.delete('sessionUid');
            window.history.replaceState({}, '', url);
          }
          clearActiveSession();
        }
      }

      // Resolve which UI to render post-validation. On success/terminal
      // we fall through to READY so the Screen (start / summary /
      // monitoring) can render. On transient failure we stay in
      // RECONNECTING until the retry loop succeeds.
      setValidationPhase(resolveValidationPhase(result));

      // Mark bootstrap complete — unlocks URL sync cleanup
      session.markBootstrapComplete();
    })();
  }, []); // Run only once on mount

  // Retry loop for the RECONNECTING phase. Each attempt calls the same
  // `rehydrateSession` the bootstrap used; success (or a definitive
  // failure) flips the phase to READY and stops the loop. The delay
  // schedule lives in utils/sessionValidationPhase.js (2 s → 4 s → 8 s
  // → 15 s → 30 s repeating). The loop cancels cleanly on unmount and
  // whenever the phase changes to something other than RECONNECTING.
  useEffect(() => {
    if (validationPhase !== VALIDATION_PHASES.RECONNECTING) return undefined;

    let cancelled = false;
    const delay = reconnectDelayFor(reconnectAttempt);
    const timer = setTimeout(async () => {
      if (cancelled) return;
      console.log(`[App] 🔁 Reconnect attempt ${reconnectAttempt + 1} (after ${delay}ms)`);
      const result = await rehydrateSessionRef.current();
      if (cancelled) return;
      if (result.success) {
        console.log('[App] ✅ Reconnected — validation resumed');
        setReconnectAttempt(0);
        setValidationPhase(VALIDATION_PHASES.READY);
        return;
      }
      if (result.transient) {
        // Still offline — bump the attempt counter to schedule the
        // next backoff step. The effect re-runs because reconnectAttempt
        // is in the dependency array.
        setReconnectAttempt((n) => n + 1);
        return;
      }
      // Definitive failure — same terminal cleanup as bootstrap, then
      // fall through to READY so the Screen renders the fresh start
      // (or SUMMARY) view.
      if (isTerminalRehydrateReason(result.reason)) {
        const url = new URL(window.location);
        if (url.searchParams.get('sessionUid')) {
          url.searchParams.delete('sessionUid');
          window.history.replaceState({}, '', url);
        }
        clearActiveSession();
      }
      setReconnectAttempt(0);
      setValidationPhase(VALIDATION_PHASES.READY);
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [validationPhase, reconnectAttempt]);

  // "Start over" escape hatch from the reconnecting view — wipes local
  // session state and returns to the fresh start screen. Never called
  // in the happy path; only surfaces when the user has been waiting
  // long enough that they'd rather rescan than keep waiting.
  const handleValidationStartOver = useCallback(() => {
    console.log('[App] User chose Start over from reconnecting view');
    // An explicit abandonment: release the attempt on the server too, or it
    // holds the connector against this driver for 10-15 minutes. Only if it is
    // still pre-charge (checked server-side); fire-and-forget so a flaky
    // network -- often why this view is showing -- never traps the driver.
    const abandonedUid = session.state.sessionUid;
    if (abandonedUid) {
      cancelIfStillPreCharge(abandonedUid).then((r) => {
        console.log('[App] Start over: server-side release', { sessionUid: abandonedUid, ...r });
      });
    }
    // Drop the URL sessionUid so a subsequent reload doesn't reload
    // the same dead recovery.
    const url = new URL(window.location);
    if (url.searchParams.get('sessionUid')) {
      url.searchParams.delete('sessionUid');
      window.history.replaceState({}, '', url);
    }
    clearActiveSession();
    reset();
    setReconnectAttempt(0);
    setValidationPhase(VALIDATION_PHASES.READY);
  }, [reset, session.state.sessionUid]);

  // No cancel on beforeunload. The browser cannot tell a reload from a close,
  // and a reload (pull-to-refresh, the OS restoring a backgrounded tab) must
  // not end a valid attempt: the old handler beaconed a cancel at every
  // pre-charge step, including the plug-in screen after the charger had
  // accepted RemoteStart, which voided the payment of a session that was
  // about to charge (GCEV3113569 follow-up). Abandonment is released by the
  // explicit Cancel / Start over paths (server-checked, useCancelAttempt), by
  // recovery of a stale attempt, by the server reclaiming this driver's
  // attempt on their next create, and by the server's own timeouts.

  // Memoised handler for "Preparing" so it doesn't change every render
  // setLoading(false) MUST run even when the body throws. Without a finally,
  // any exception between the two calls strands the driver on the Preloader
  // permanently: App renders `loading || !Screen ? <Preloader/>`, so the screen
  // becomes three dots on white while the rest of the app carries on polling.
  // Observed on staging — station_status said Available and the OCPP socket was
  // open, and the driver still saw a spinner that never resolved.
  const handlePreparing = useCallback(async () => {
    setLoading(true);
    try {
      await simulateWait(600);
      setStepRef.current(STEPS.MONITOR_CHARGING);
    } catch (err) {
      // `finally` re-throws; nothing awaits this handler. See nextStep.
      console.error('[App] preparing transition failed; staying put', err);
    } finally {
      setLoading(false);
    }
  }, []);

  //  Auto-move CONNECT_CHARGER → MONITOR_CHARGING on "Preparing" (disabled in debug mode)
  // NOTE: Modal removed - user flows directly to monitoring with inline status
  usePreparingNotification({
    enabled: step === STEPS.CONNECT_CHARGER && !!session.state.stationId && !debugMode,
    onPreparing: handlePreparing,
    showNotification: false, // Disabled - show inline status in monitoring instead
    notificationTitle: 'Charger Ready',
    notificationMessage: 'Your charger is preparing to start charging.',
  });

  //  Navigation helpers with conditional payment flow
  const nextStep = useCallback(async () => {
    setLoading(true);
    try {
    await simulateWait(500);

    // Use STEP_FORWARD map for deterministic next step resolution
    // This correctly handles decimal steps like 2.5 → 3
    let nextStepValue = STEP_FORWARD[step] ?? MAX_STEP;

    // ── Account-choice routing ──────────────────────────────────────
    //
    // Leaving ACCOUNT_CHOICE, honour the connector routing EvProfile
    // resolved at Get Started tap time. It is stashed rather than acted on
    // immediately so the account question is asked before any session can
    // exist; it remains click-time truth, and ConnectorSelection re-checks
    // staleness itself when it renders.
    if (step === STEPS.ACCOUNT_CHOICE
        && session.state.pendingConnectorRouting === 'choose') {
      nextStepValue = STEPS.CONNECTOR_SELECTION;
    }

    // A guest has no plate to type and no saved vehicles to pick, so
    // CHARGE_SETUP would be an empty screen with an "(optional)" field.
    // Skip it. Guests still choose a budget — a pre-authorisation is always
    // required, so there is no "straight to the card" path to take. Session
    // creation moves with them, to AmountPicker's ensure boundary.
    if (isGuestChoice(session.state.accountChoice)
        && nextStepValue === STEPS.CHARGE_SETUP) {
      nextStepValue = STEPS.AMOUNT_PICKER;
    }

    // Skip payment steps when payments are disabled
    if (!PAYMENTS_ENABLED) {
      // If moving from CHARGE_SETUP, skip directly to CONNECT_CHARGER
      if (step === STEPS.CHARGE_SETUP) {
        nextStepValue = STEPS.CONNECT_CHARGER;
      }
    } else {
      // Payments are enabled - check vehicle billing mode
      const vehicleState = session.state.vehicle;
      
      // CRITICAL FIX: Skip payment steps for vehicles that don't require payment
      // This handles ALL transitions, not just from VEHICLE_VERIFICATION
      if (!shouldShowPaymentStep(vehicleState)) {
        // Vehicle verified with FLEET_INVOICE or FREE - skip amount picker AND payment
        if (step === STEPS.VEHICLE_VERIFICATION || step === STEPS.AMOUNT_PICKER || step === STEPS.MAKE_PAYMENT) {
          console.log('[App] ✅ Skipping payment steps - vehicle does not require payment:', {
            currentStep: step,
            stepName: Object.keys(STEPS).find(k => STEPS[k] === step),
            billingMode: vehicleState?.billingMode,
            paymentRequired: vehicleState?.paymentRequired
          });
          nextStepValue = STEPS.CONNECT_CHARGER;
        }
      }
    }

    setStepRef.current(Math.min(nextStepValue, MAX_STEP));
    } catch (err) {
      // `finally` alone clears the flag but RE-THROWS, so a failure here
      // escaped as an unhandled promise rejection — nothing awaits the
      // handler a button's onClick returns. The driver saw no error and the
      // browser logged one nobody owned. Keeping the driver on the current
      // step is the right recovery: the transition did not happen, so the
      // screen should not claim it did.
      console.error('[App] step transition failed; staying on the current step', err);
    } finally {
      setLoading(false);
    }
  }, [step, session.state.vehicle, session.state.accountChoice, session.state.pendingConnectorRouting]);

  const prevStep = useCallback(async () => {
    setLoading(true);
    try {
    await simulateWait(500);

    // Use STEP_BACK map for deterministic previous step resolution
    // This correctly handles decimal steps like 2.5 → 2
    let prevStepValue = STEP_BACK[step] ?? MIN_STEP;

    // A guest never saw CHARGE_SETUP on the way forward, so back must not
    // drop them into it — they would land on a plate field their path does
    // not use. Send them where they actually came from.
    if (isGuestChoice(session.state.accountChoice)
        && prevStepValue === STEPS.CHARGE_SETUP) {
      prevStepValue = session.state.pendingConnectorRouting === 'choose'
        ? STEPS.CONNECTOR_SELECTION
        : STEPS.ACCOUNT_CHOICE;
    }

    // Skip payment steps when payments are disabled
    if (!PAYMENTS_ENABLED) {
      // If moving back from CONNECT_CHARGER, skip directly to CHARGE_SETUP
      if (step === STEPS.CONNECT_CHARGER) {
        prevStepValue = STEPS.CHARGE_SETUP;
      }
    } else {
      // Payments are enabled - check vehicle billing mode for back navigation
      const vehicleState = session.state.vehicle;
      
      if (step === STEPS.CONNECT_CHARGER && !shouldShowPaymentStep(vehicleState)) {
        // Vehicle verified with FLEET_INVOICE or FREE - skip payment AND amount picker when going back
        // Go back to VEHICLE_VERIFICATION (step 2.5) if verified, otherwise CHARGE_SETUP (step 2)
        console.log('[App] Skipping payment and amount picker on back - vehicle does not require payment');
        prevStepValue = vehicleState?.verified ? STEPS.VEHICLE_VERIFICATION : STEPS.CHARGE_SETUP;
      }
      // Skip amount picker when going back from MAKE_PAYMENT if payment not required
      else if (step === STEPS.MAKE_PAYMENT && !shouldShowPaymentStep(vehicleState)) {
        console.log('[App] Skipping amount picker on back - vehicle does not require payment');
        prevStepValue = vehicleState?.verified ? STEPS.VEHICLE_VERIFICATION : STEPS.CHARGE_SETUP;
      }
    }

    setStepRef.current(Math.max(prevStepValue, MIN_STEP));
    } catch (err) {
      // Same as nextStep: `finally` re-throws, and nothing awaits the
      // handler a Back button's onClick returns.
      console.error('[App] back transition failed; staying on the current step', err);
    } finally {
      setLoading(false);
    }
  }, [step, session.state.vehicle, session.state.accountChoice, session.state.pendingConnectorRouting]);


  //  Reset scroll position when step changes
  useEffect(() => {
    const scrollArea = document.querySelector('.ev-inner-profile-wrapper');
    if (scrollArea) scrollArea.scrollTop = 0;
  }, [step]);

  //  Resolve current screen
  // Rollout flag OFF: the account-choice screen is never shown, whatever
  // step value reaches here.
  const config = (!ACCOUNT_CHOICE_ENABLED && step === STEPS.ACCOUNT_CHOICE)
    ? STEP_MAP[STEPS.EV_PROFILE]
    : STEP_MAP[step];
  const Screen = config?.component;

  //  Manual step control for development
  const goToStep = useCallback((targetStep) => {
    setStepRef.current(targetStep);
  }, []);

  return (
    // The main landmark of the standalone charging flow (it renders outside
    // AppShell, which owns the landmark for the dashboard).
    <main className="app-wrapper">
      {/* Debug Step Control Panel - Only shown when ?debug is in URL */}
      {showDebugPanel && (
        <DebugStepControl
          currentStep={step}
          currentSubStep={paymentStep}
          stepMap={STEP_MAP}
          steps={STEPS}
          onStepChange={goToStep}
          onSubStepChange={setPaymentStep}
          debugMode={debugMode}
          onDebugModeChange={setDebugMode}
        />
      )}

      {/* Active session banner — shown when user has an active charging session */}
      {activeSessionConflict && !loading && (
        <ActiveSessionBanner
          sessionUid={activeSessionConflict.sessionUid}
          stationId={activeSessionConflict.stationId}
          status={activeSessionConflict.status}
          startedAt={activeSessionConflict.startedAt}
          onResume={handleResumeActiveSession}
          onDismiss={handleDismissActiveSession}
        />
      )}

      <div className="app-container">
        {session.state.authFailed ? (
          /*
            SESSION_AUTH_REQUIRED containment. Rendered instead of the
            current Screen so its polling intervals / SSE / retry loops
            unmount and stop firing failed requests. The flag is set
            by the shared axios interceptor in SessionContext.
          */
          <AuthRequiredView />
        ) : loading || !Screen ? (
          <Preloader />
        ) : validationPhase === VALIDATION_PHASES.VALIDATING
             || validationPhase === VALIDATION_PHASES.RECONNECTING ? (
          /*
            Bootstrap has recovered a sessionUid HINT (from URL,
            localStorage, or beacon) but backend validation has not
            confirmed the session is still valid. Rendering the
            charging Screen here would let a stale local snapshot
            look authoritative — instead we render the validation
            gate. See utils/sessionValidationPhase.js for the phase
            state machine.
          */
          <SessionValidationGate
            phase={validationPhase}
            lastKnown={{
              sessionUid: session.state.sessionUid,
              stationId: session.state.stationId,
              publicId: session.state.publicId,
              startedAt: session.state.live?.startTime || null,
              energyKWh: typeof session.state.live?.energyKWh === 'number'
                ? session.state.live.energyKWh
                : null,
            }}
            onStartOver={handleValidationStartOver}
          />
        ) : sessionUid && tabRole === 'readonly' ? (
          /*
            M4 — Another tab currently owns this sessionUid. Render a
            dedicated read-only view so charging actions cannot fire
            from two tabs simultaneously. Backend idempotency is still
            authoritative; this is a UX guard.
          */
          <TabOwnershipBanner onTakeover={requestOwnership} />
        ) : (
          <Screen
            {...(config.navigation && {
              handleIncreaseStep: nextStep,
              handleDecreaseStep: prevStep,
            })}
          />
        )}
      </div>
    </main>
  );
}
